import { Image as ExpoImage } from 'expo-image';
import { Redirect, useLocalSearchParams } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { ShieldAlert, UserPlus } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { InternalNotFound } from '@/src/components/InternalNotFound';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  FRIENZZI_BOT_NAME,
  FRIENZZI_BOT_UID,
} from '@/src/features/bot/bot-identity';
import { CommentComposeModal } from '@/src/features/comments/CommentComposeModal';
import { GarageSection } from '@/src/features/garage/GarageCard';
import { useUserGarageVehicles } from '@/src/features/garage/use-garage';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import {
  PhotoViewer,
  type PhotoContext,
} from '@/src/features/posts/PhotoAttachment';
import type { PublicPost } from '@/src/features/posts/post-service';
import { useUserPosts } from '@/src/features/posts/use-user-posts';
import {
  getProfileAvatarOriginalURL,
  getProfileAvatarURL,
  getProfileCoverOriginalURL,
  getProfileCoverPhotoURL,
  emptyProfileReactionCounts,
  totalProfileReactions,
  type ProfileConnectionKind,
} from '@/src/features/profile/profile-service';
import {
  ProfileContentTabs,
  type ProfileContentTab,
} from '@/src/features/profile/ProfileContentTabs';
import { ProfileStatsSummary } from '@/src/features/profile/ProfileStatsSummary';
import {
  ProfileConnectionsModal,
  ProfileReactionBreakdownModal,
} from '@/src/features/profile/ProfileStatsModals';
import {
  useBlockState,
  useBlockUser,
  useFollowState,
  usePublicProfile,
  usePublicProfileStats,
  useToggleFollow,
  useUnblockUser,
} from '@/src/features/profile/use-profile';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDateOnly } from '@/src/utils/dates';

function BotUnfollowConfirmModal({
  loading,
  onCancel,
  onConfirm,
  visible,
}: {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}
    >
      <Pressable style={styles.confirmModalBackdrop} onPress={onCancel}>
        <Pressable
          style={styles.confirmModal}
          onPress={(event) => event.stopPropagation()}
        >
          <Text style={styles.confirmModalTitle}>
            Unfollow {FRIENZZI_BOT_NAME}?
          </Text>
          <Text style={styles.confirmModalText}>
            You will stop seeing Frienzzi Bot updates in your Frienzzone feed.
            You can follow Frienzzi Bot again anytime.
          </Text>
          <View style={styles.confirmModalActions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={loading}
              onPress={onCancel}
              style={styles.confirmModalButton}
            />
            <Button
              title="Unfollow"
              variant="danger"
              loading={loading}
              onPress={onConfirm}
              style={styles.confirmModalButton}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

type ProfileViewerImage = {
  accessibilityLabel: string;
  analyticsContext: PhotoContext;
  originalURL: string;
  thumbnailURL: string | null;
  title: string;
};

export default function PublicProfileScreen() {
  const params = useLocalSearchParams<{ uid?: string }>();
  const uid = Array.isArray(params.uid) ? params.uid[0] : params.uid;
  const { user } = useAuth();
  const isFocused = useIsFocused();
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState<ProfileContentTab>('posts');
  const blockStateQuery = useBlockState(user?.uid, uid);
  const blockState = blockStateQuery.data;
  const profileAvailable = !blockState?.blocked;
  const profileQuery = usePublicProfile(uid, profileAvailable);
  const statsQuery = usePublicProfileStats(uid, profileAvailable);
  const userPosts = useUserPosts(
    uid,
    user?.uid,
    undefined,
    profileAvailable && isFocused && activeTab === 'posts',
  );
  const garageVehicles = useUserGarageVehicles(
    uid,
    profileAvailable && isFocused && activeTab === 'garage',
  );
  const refetchProfile = profileQuery.refetch;
  const refetchStats = statsQuery.refetch;
  const refreshUserPosts = userPosts.refresh;
  const followState = useFollowState(user?.uid, uid);
  const toggleFollow = useToggleFollow(user?.uid, uid);
  const blockUserMutation = useBlockUser(user?.uid, uid);
  const unblockUserMutation = useUnblockUser(user?.uid, uid);
  const followActionPending = followState.loading || toggleFollow.isPending;
  const blockActionPending =
    blockUserMutation.isPending || unblockUserMutation.isPending;
  const listRef = useRef<FlatList<PublicPost>>(null);
  const [confirmingBotUnfollow, setConfirmingBotUnfollow] = useState(false);
  const [commentPost, setCommentPost] = useState<PublicPost | null>(null);
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const [connectionsKind, setConnectionsKind] =
    useState<ProfileConnectionKind | null>(null);
  const [showReactionBreakdown, setShowReactionBreakdown] = useState(false);
  const [viewerImage, setViewerImage] = useState<ProfileViewerImage | null>(
    null,
  );
  const isFrienzziBot = uid === FRIENZZI_BOT_UID;
  const profileFullName =
    profileQuery.data?.fullName ||
    (isFrienzziBot ? FRIENZZI_BOT_NAME : 'Frienzzi user');

  useEffect(() => {
    if (followState.error) {
      showError(followState.error, 'Unable to update follow status.');
    }
  }, [followState.error, showError]);

  const refreshProfile = useCallback(() => {
    void refetchProfile();
    void refetchStats();
    refreshUserPosts();
  }, [refetchProfile, refetchStats, refreshUserPosts]);

  const setFollowing = useCallback(
    (nextFollowing: boolean) => {
      if (followActionPending) {
        return;
      }

      setConfirmingBotUnfollow(false);
      toggleFollow.mutate(
        { following: nextFollowing },
        {
          onSuccess: () => {
            showSuccess(
              nextFollowing
                ? `You are now following ${profileFullName}.`
                : `You unfollowed ${profileFullName}.`,
              'Frienzzone updated',
            );
            trackEvent('follow_toggle', {
              following: nextFollowing,
            });
          },
          onError: (error) => {
            showError(error, 'Unable to update follow status.');
          },
        },
      );
    },
    [
      followActionPending,
      profileFullName,
      showError,
      showSuccess,
      toggleFollow,
    ],
  );

  const handleBlock = useCallback(() => {
    if (blockActionPending) {
      return;
    }

    blockUserMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess(`${profileFullName} is blocked.`, 'Profile blocked');
        trackEvent('profile_block', { target_is_current_user: false });
      },
      onError: (error) => {
        showError(error, 'Unable to block this profile.');
      },
    });
  }, [
    blockActionPending,
    blockUserMutation,
    profileFullName,
    showError,
    showSuccess,
  ]);

  const handleUnblock = useCallback(() => {
    if (blockActionPending) {
      return;
    }

    unblockUserMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess(`${profileFullName} is unblocked.`, 'Profile unblocked');
        refreshProfile();
      },
      onError: (error) => {
        showError(error, 'Unable to unblock this profile.');
      },
    });
  }, [
    blockActionPending,
    profileFullName,
    refreshProfile,
    showError,
    showSuccess,
    unblockUserMutation,
  ]);

  if (uid === user?.uid) {
    return <Redirect href="/profile" />;
  }

  if (profileQuery.isLoading) {
    return <LoadingView message="Loading profile..." />;
  }

  if (blockState?.blocked) {
    return (
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <View style={styles.blockedProfilePanel}>
          <Text style={styles.blockedProfileTitle}>
            {blockState.blockedByViewer
              ? 'You blocked this profile'
              : 'Profile unavailable'}
          </Text>
          <Text style={styles.blockedProfileText}>
            {blockState.blockedByViewer
              ? 'You will not see each other on Frienzzi unless you unblock this profile.'
              : 'This profile is not available.'}
          </Text>
          {blockState.blockedByViewer ? (
            <Button
              title="Unblock"
              variant="secondary"
              loading={blockActionPending}
              onPress={handleUnblock}
            />
          ) : null}
        </View>
      </Screen>
    );
  }

  if (profileQuery.isError) {
    return (
      <Screen>
        <ErrorView
          message={profileQuery.error.message}
          onRetry={() => {
            void profileQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  const profile = profileQuery.data;

  if (!uid || !profile) {
    return (
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <InternalNotFound message="This profile may have moved, been deleted, or is no longer available." />
      </Screen>
    );
  }

  const stats = statsQuery.data ?? {
    blockedByCount: 0,
    followerCount: 0,
    followingCount: 0,
    inviteCount: 0,
    reactionCounts: emptyProfileReactionCounts(),
    totalLikeCount: 0,
  };
  const reactionTotal = totalProfileReactions(stats);
  const fullName = profileFullName;
  const avatarInitial = fullName.slice(0, 1).toUpperCase();
  const avatarURL = getProfileAvatarURL(profile, 'display');
  const avatarOriginalURL = getProfileAvatarOriginalURL(profile);
  const coverPhotoURL = getProfileCoverPhotoURL(profile, 'hero');
  const coverPhotoOriginalURL = getProfileCoverOriginalURL(profile);

  return (
    <>
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <FlatList
          ref={listRef}
          contentContainerStyle={styles.feedContent}
          data={activeTab === 'posts' ? userPosts.posts : []}
          keyExtractor={(post) => post.id}
          ListHeaderComponent={
            <>
              <View style={styles.hero}>
                <View style={styles.cover}>
                  {coverPhotoURL ? (
                    <Pressable
                      accessibilityLabel="Profile cover photo"
                      accessibilityRole="imagebutton"
                      onPress={() => {
                        setViewerImage({
                          accessibilityLabel: 'Profile cover photo',
                          analyticsContext: 'profile_cover',
                          originalURL: coverPhotoOriginalURL ?? coverPhotoURL,
                          thumbnailURL: coverPhotoURL,
                          title: 'Profile cover photo',
                        });
                      }}
                      style={({ pressed }) => [
                        styles.coverImage,
                        pressed ? styles.photoPressed : null,
                      ]}
                    >
                      <ExpoImage
                        cachePolicy="memory-disk"
                        contentFit="cover"
                        source={{ uri: coverPhotoURL }}
                        style={styles.coverImage}
                        transition={160}
                      />
                    </Pressable>
                  ) : (
                    <View style={styles.coverPlaceholder}>
                      <Text style={styles.coverPlaceholderText}>Frienzzi</Text>
                    </View>
                  )}
                </View>
                <View style={styles.identityRow}>
                  <View style={styles.avatarFrame}>
                    {avatarURL ? (
                      <Pressable
                        accessibilityLabel="Profile photo"
                        accessibilityRole="imagebutton"
                        onPress={() => {
                          setViewerImage({
                            accessibilityLabel: 'Profile photo',
                            analyticsContext: 'profile_avatar',
                            originalURL: avatarOriginalURL ?? avatarURL,
                            thumbnailURL: avatarURL,
                            title: 'Profile photo',
                          });
                        }}
                        style={({ pressed }) => [
                          styles.avatarImage,
                          pressed ? styles.photoPressed : null,
                        ]}
                      >
                        <ExpoImage
                          cachePolicy="memory-disk"
                          contentFit="cover"
                          source={{ uri: avatarURL }}
                          style={styles.avatarImage}
                          transition={160}
                        />
                      </Pressable>
                    ) : (
                      <Text style={styles.avatarText}>{avatarInitial}</Text>
                    )}
                  </View>
                  <View style={styles.identityText}>
                    <IdentityHeader
                      containerStyle={styles.profileIdentityHeader}
                      density="profile"
                      founderBadge={profile.founderBadge === true}
                      fullName={fullName}
                      interactive={false}
                      showAvatar={false}
                      username={profile.username}
                      verifiedBadge={profile.verifiedBadge === true}
                    />
                    <Text style={styles.metaText}>
                      Joined {formatDateOnly(profile.createdAt)}
                    </Text>
                  </View>
                </View>
                {profile.bio ? (
                  <Text style={styles.bio}>{profile.bio}</Text>
                ) : null}
                <View style={styles.followRow}>
                  <Button
                    title={followState.following ? 'Following' : 'Follow'}
                    variant={followState.following ? 'secondary' : 'primary'}
                    disabled={followActionPending}
                    onPress={() => {
                      const nextFollowing = !followState.following;

                      if (isFrienzziBot && !nextFollowing) {
                        setConfirmingBotUnfollow(true);
                        return;
                      }

                      setFollowing(nextFollowing);
                    }}
                    style={styles.followButton}
                  />
                  {!isFrienzziBot ? (
                    <Button
                      title="Block"
                      variant="danger"
                      loading={blockUserMutation.isPending}
                      disabled={blockActionPending}
                      onPress={handleBlock}
                      style={[styles.followButton, styles.blockButton]}
                    />
                  ) : null}
                </View>
                <ProfileStatsSummary
                  loading={statsQuery.isLoading}
                  primaryStats={[
                    {
                      key: 'followers',
                      label: 'Followers',
                      onPress: () => setConnectionsKind('followers'),
                      value: stats.followerCount,
                    },
                    {
                      key: 'following',
                      label: 'Following',
                      onPress: () => setConnectionsKind('following'),
                      value: stats.followingCount,
                    },
                    {
                      key: 'reactions',
                      label: 'Reactions',
                      onPress: () => setShowReactionBreakdown(true),
                      value: reactionTotal,
                    },
                  ]}
                  secondaryInfoItems={[
                    {
                      label: 'Invites',
                      description:
                        "People who joined Frienzzi from this profile's invite link.",
                    },
                    {
                      label: 'Blocked by',
                      description:
                        'Signed-in users who currently block this profile. Blocker names are never shown.',
                    },
                  ]}
                  secondaryStats={[
                    {
                      Icon: UserPlus,
                      key: 'invites',
                      label: 'Invites',
                      onPress: () => setConnectionsKind('invites'),
                      value: stats.inviteCount,
                    },
                    {
                      Icon: ShieldAlert,
                      key: 'blockedBy',
                      label: 'Blocked by',
                      value: stats.blockedByCount,
                    },
                  ]}
                />
              </View>

              <ProfileContentTabs
                activeTab={activeTab}
                onChange={setActiveTab}
              />
              {activeTab === 'posts' &&
              (userPosts.loading || userPosts.error) ? (
                <View style={styles.postsSection}>
                  {userPosts.loading ? (
                    <Text style={styles.mutedText}>Loading posts...</Text>
                  ) : null}
                  {userPosts.error ? (
                    <ErrorView message={userPosts.error.message} />
                  ) : null}
                </View>
              ) : null}
              {activeTab === 'garage' ? (
                <>
                  <GarageSection
                    emptyText="Public Garage vehicles from this profile will appear here."
                    loading={garageVehicles.loading}
                    vehicles={garageVehicles.vehicles}
                  />
                  {garageVehicles.error ? (
                    <ErrorView message={garageVehicles.error.message} />
                  ) : null}
                </>
              ) : null}
            </>
          }
          ListEmptyComponent={
            activeTab === 'posts' && !userPosts.loading && !userPosts.error ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No posts yet</Text>
                <Text style={styles.emptyText}>
                  Public posts from this profile will appear here.
                </Text>
              </View>
            ) : null
          }
          ListFooterComponent={
            activeTab === 'posts' && userPosts.loadingMore ? (
              <View style={styles.feedFooter}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
          onEndReached={activeTab === 'posts' ? userPosts.loadMore : undefined}
          onEndReachedThreshold={0.4}
          onRefresh={refreshProfile}
          refreshing={
            profileQuery.isRefetching ||
            statsQuery.isRefetching ||
            userPosts.refreshing
          }
          renderItem={({ item }) => (
            <PostCard
              post={item}
              onCommentPress={setCommentPost}
              onPhotoPress={(post, photoIndex) => {
                setPhotoViewerInitialPost(post);
                setPhotoViewerPostId(post.id);
                setPhotoViewerInitialIndex(photoIndex);
              }}
              onVideoPress={(post) => {
                setVideoViewerInitialPost(post);
                setVideoViewerPostId(post.id);
              }}
            />
          )}
          showsVerticalScrollIndicator={false}
        />
      </Screen>
      <BotUnfollowConfirmModal
        loading={false}
        onCancel={() => setConfirmingBotUnfollow(false)}
        onConfirm={() => setFollowing(false)}
        visible={confirmingBotUnfollow}
      />
      <CommentComposeModal
        post={commentPost}
        visible={Boolean(commentPost)}
        onClose={() => setCommentPost(null)}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        hasMorePosts={userPosts.hasMore}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        loadingMorePosts={userPosts.loadingMore}
        posts={userPosts.posts}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={setCommentPost}
        onLoadMorePosts={userPosts.loadMore}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        hasMorePosts={userPosts.hasMore}
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        loadingMorePosts={userPosts.loadingMore}
        posts={userPosts.posts}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={setCommentPost}
        onLoadMorePosts={userPosts.loadMore}
      />
      <ProfileConnectionsModal
        currentUid={user?.uid}
        kind={connectionsKind}
        profileUid={uid}
        totalCount={
          connectionsKind === 'following'
            ? stats.followingCount
            : connectionsKind === 'invites'
              ? stats.inviteCount
              : stats.followerCount
        }
        visible={Boolean(connectionsKind)}
        onClose={() => setConnectionsKind(null)}
      />
      <ProfileReactionBreakdownModal
        stats={stats}
        visible={showReactionBreakdown}
        onClose={() => setShowReactionBreakdown(false)}
      />
      {viewerImage ? (
        <PhotoViewer
          accessibilityLabel={viewerImage.accessibilityLabel}
          analyticsContext={viewerImage.analyticsContext}
          onClose={() => setViewerImage(null)}
          originalURL={viewerImage.originalURL}
          thumbnailURL={viewerImage.thumbnailURL}
          title={viewerImage.title}
          visible
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  feedScreenContent: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: '100%',
  },
  feedContent: {
    alignSelf: 'center',
    gap: spacing.lg,
    maxWidth: 640,
    paddingBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  feedFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  blockedProfilePanel: {
    alignSelf: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    marginTop: spacing.xl,
    maxWidth: 520,
    padding: spacing.lg,
    width: '100%',
  },
  blockedProfileTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  blockedProfileText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  hero: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cover: {
    backgroundColor: colors.surfaceTint,
    height: 164,
    width: '100%',
  },
  coverImage: {
    height: '100%',
    width: '100%',
  },
  photoPressed: {
    opacity: 0.9,
  },
  coverPlaceholder: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    flex: 1,
    justifyContent: 'center',
  },
  coverPlaceholderText: {
    color: colors.surface,
    fontSize: 24,
    fontWeight: '900',
  },
  identityRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: -36,
    paddingHorizontal: spacing.md,
  },
  avatarFrame: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderColor: colors.surface,
    borderRadius: 46,
    borderWidth: 4,
    height: 92,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 92,
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.secondary,
    fontSize: 34,
    fontWeight: '900',
  },
  identityText: {
    flex: 1,
    gap: spacing.xs,
    marginTop: 38,
  },
  profileIdentityHeader: {
    alignSelf: 'stretch',
  },
  metaText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  bio: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.lg,
  },
  followRow: {
    alignItems: 'stretch',
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.lg,
  },
  followButton: {
    flex: 1,
    minWidth: 0,
  },
  blockButton: {
    backgroundColor: colors.text,
    borderColor: colors.text,
  },
  postsSection: {
    gap: spacing.md,
  },
  mutedText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  confirmModalBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(16, 8, 36, 0.48)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  confirmModal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.lg,
    width: '100%',
  },
  confirmModalTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  confirmModalText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  confirmModalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  confirmModalButton: {
    flex: 1,
  },
});
